import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import NotFound from '@/pages/NotFound';
import { resolvePaperPdf, createPaperPdfSignedUrl } from '@/lib/api/paper-pdf-access';
import { Loader2 } from 'lucide-react';

type State =
  | { kind: 'loading' }
  | { kind: 'signin-required'; paperId: string }
  | { kind: 'not-found' };

/**
 * Bookmarkable route that resolves a paper's PDF for the current user:
 *   - external / arXiv URL -> redirect
 *   - allowed + storage-hosted -> mint a signed URL and redirect
 *   - denied while signed-in -> render 404
 *   - denied while logged-out and the paper is private -> show a sign-in
 *     prompt that returns the user here after login
 */
export default function PaperPdfRedirect() {
  const { id, versionId } = useParams<{ id: string; versionId?: string }>();
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    if (!id || loading) return;
    let cancelled = false;

    (async () => {
      const resolution = await resolvePaperPdf(id, versionId ?? null);
      if (cancelled) return;

      if (!resolution || resolution.reason === 'not_found' || resolution.reason === 'version_not_found') {
        setState({ kind: 'not-found' });
        return;
      }

      if (resolution.external_url) {
        window.location.replace(resolution.external_url);
        return;
      }

      if (!resolution.allowed) {
        if (!user && resolution.paper_visibility === 'private') {
          setState({ kind: 'signin-required', paperId: id });
        } else {
          setState({ kind: 'not-found' });
        }
        return;
      }

      if (!resolution.storage_path) {
        setState({ kind: 'not-found' });
        return;
      }

      const signed = await createPaperPdfSignedUrl(
        resolution.storage_path,
        resolution.filename,
        600,
      );
      if (cancelled) return;
      if (!signed) {
        setState({ kind: 'not-found' });
        return;
      }
      window.location.replace(signed);
    })();

    return () => {
      cancelled = true;
    };
  }, [id, versionId, user, loading]);

  if (state.kind === 'not-found') {
    return <NotFound />;
  }

  if (state.kind === 'signin-required') {
    const redirectTo = location.pathname + location.search;
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center space-y-6">
          <h1 className="text-2xl font-medium">Sign in to view this manuscript</h1>
          <p className="text-muted-foreground">
            This paper is private. If you were invited or share it with an
            account that has access, sign in to open the PDF.
          </p>
          <div className="flex justify-center gap-3">
            <Link
              to={`/auth?redirectTo=${encodeURIComponent(redirectTo)}`}
              className="inline-flex items-center rounded-md bg-primary px-4 py-2 text-primary-foreground hover:opacity-90"
            >
              Sign in
            </Link>
            <button
              type="button"
              onClick={() => navigate(`/paper/${state.paperId}`)}
              className="inline-flex items-center rounded-md border px-4 py-2 hover:bg-accent"
            >
              View paper page
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );
}
